import { HomePackages } from "@/components/common/HomePackages";
import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePhones } from "@/lib/contacts";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Plane,
  ShieldCheck,
  Clock,
  Globe2,
  Star,
  Phone,
  Mail,
  Quote,
} from "lucide-react";
import Navbar from "@/components/layout/Navbar";
// Marketing landing page — brand redesign (VENKA blue→green)
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { SERVICES } from "@/lib/services";

const HERO_IMG =
  "https://images.unsplash.com/photo-1593182440709-4b7b56482c55?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920&ixlib=rb-4.1.0";
const PASSPORT_IMG =
  "https://images.unsplash.com/photo-1641736494066-bd18237ede26?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&ixlib=rb-4.1.0";

const DESTINATIONS = [
  {
    key: "france",
    name: "France",
    label: "Study · Work · Visit",
    img: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&ixlib=rb-4.1.0",
    span: "md:col-span-2 md:row-span-2",
  },
  {
    key: "uk",
    name: "United Kingdom",
    label: "Skilled Worker & Student",
    img: "https://images.unsplash.com/photo-1549483249-f0b359d1e289?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&ixlib=rb-4.1.0",
    span: "md:col-span-2",
  },
  {
    key: "canada",
    name: "Canada",
    label: "Immigration Programs",
    img: "https://images.unsplash.com/photo-1636829504058-2315a3849412?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&ixlib=rb-4.1.0",
    span: "",
  },
  {
    key: "usa",
    name: "United States",
    label: "Visitor & Study Visas",
    img: "https://images.unsplash.com/photo-1508009603885-50cf7c579365?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&ixlib=rb-4.1.0",
    span: "",
  },
];

const STATS = [
  { value: "12+", label: "Years of experience" },
  { value: "5k+", label: "Applications processed" },
  { value: "40+", label: "Destination countries" },
  { value: "98%", label: "Client satisfaction" },
];

const WHY = [
  { icon: ShieldCheck, title: "Accurate Guidance", desc: "Skilled professionals ready to provide reliable, up-to-date advice on every route." },
  { icon: Clock, title: "Fast Turnaround", desc: "We move quickly so your documents are ready sooner than you expect." },
  { icon: Globe2, title: "Global Reach", desc: "Based in Angola, serving an international network of customers worldwide." },
];

const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

export const HomePage = () => {
  const { t } = useTranslation();
  const PHONES = usePhones();

  return (
    <div className="min-h-screen bg-background text-foreground font-sans flex flex-col">
      <Navbar />
      <main className="flex-1">
        {/* HERO */}
        <section className="relative overflow-hidden" data-testid="home-hero">
          <div className="absolute inset-0">
            <img src={HERO_IMG} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-venka-ink via-venka-ink/85 to-venka-ink/30" />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
          </div>
          <div className="relative mx-auto max-w-7xl px-6 py-28 lg:py-36">
            <motion.div initial="hidden" animate="show" className="max-w-2xl">
              <motion.div variants={fadeUp} custom={0} className="inline-flex items-center gap-2 rounded-full border border-venka-green/40 bg-venka-green/10 px-4 py-1.5 text-xs text-venka-green-light">
                <span className="h-1.5 w-1.5 rounded-full bg-venka-green-light animate-pulse" />
                <span data-testid="home-hero-eyebrow">Travel Documents & Visa Specialists</span>
              </motion.div>
              <motion.h1 variants={fadeUp} custom={1} className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] text-brand-gradient" data-testid="home-hero-title">
                {t("hero.title")}
              </motion.h1>
              <motion.p variants={fadeUp} custom={2} className="mt-5 text-base text-slate-300 max-w-xl leading-relaxed" data-testid="home-hero-subtitle">
                {t("hero.subtitle")}
              </motion.p>
              <motion.div variants={fadeUp} custom={3} className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" className="rounded-full brand-gradient text-white hover:opacity-90 px-7 shadow-xl shadow-venka-blue/30" data-testid="home-cta-apply">
                  <Link to="/apply">{t("apply.ctaOnline", "Apply Online")} <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full border-white/25 bg-white/5 text-white hover:bg-white/10 px-7" data-testid="home-cta-contact">
                  <Link to="/contact">Talk to a consultant</Link>
                </Button>
              </motion.div>
              <motion.div variants={fadeUp} custom={4} className="mt-10 flex items-center gap-2 text-sm text-slate-300">
                <span className="flex">{[0,1,2,3,4].map((i) => <Star key={i} className="h-4 w-4 fill-venka-green-light text-venka-green-light" />)}</span>
                <span className="text-slate-400">Rated excellent by our clients</span>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* PACKAGES (managed in ERP > Settings > Website packages) */}
        <HomePackages />

        {/* STATS STRIP */}
        <section className="border-y border-white/10 bg-card/40" data-testid="home-stats">
          <div className="mx-auto max-w-7xl px-6 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {STATS.map((s, i) => (
              <div key={i} className="py-8 px-4 text-center" data-testid={`home-stat-${i}`}>
                <p className="font-serif text-3xl md:text-4xl text-white">{s.value}</p>
                <p className="mt-1 text-xs text-slate-400 uppercase tracking-wide">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* SERVICES */}
        <section id="services" className="mx-auto max-w-7xl px-6 py-24" data-testid="home-services-section">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold tracking-[0.22em] uppercase text-venka-green-light mb-3">What we do</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white leading-tight">Everything you need to travel and relocate</h2>
            <p className="mt-4 text-base text-slate-400">From visa applications and certified translations to passports, flight tickets and travel insurance — expert support at every step.</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.key}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-60px" }}
                  variants={fadeUp}
                  custom={i}
                >
                  <Link
                    to={s.to}
                    className="group block h-full rounded-2xl border border-white/10 bg-card p-6 hover:border-venka-blue/50 hover:-translate-y-1 transition-[transform,border-color] duration-300 shadow-lg shadow-black/20"
                    data-testid={`home-service-card-${s.key}`}
                  >
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl brand-gradient text-white shadow-lg shadow-venka-blue/25">
                      <Icon className="h-5 w-5" strokeWidth={1.8} />
                    </span>
                    <h3 className="mt-5 font-serif text-xl text-white">{s.title}</h3>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                    <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-venka-green-light">
                      Learn more <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* DESTINATIONS BENTO */}
        <section id="destinations" className="mx-auto max-w-7xl px-6 py-12" data-testid="home-destinations-section">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
            <div className="max-w-xl">
              <p className="text-xs font-semibold tracking-[0.22em] uppercase text-venka-green-light mb-3">Favourite destinations</p>
              <h2 className="font-serif text-3xl md:text-4xl text-white leading-tight">Choose where your next chapter begins</h2>
            </div>
            <p className="text-sm text-slate-400 max-w-sm">We guide you through study, work and visitor routes for the world's most sought-after countries.</p>
          </div>
          <div className="grid md:grid-cols-4 md:auto-rows-[190px] gap-4">
            {DESTINATIONS.map((d) => (
              <Link
                key={d.key}
                to="/visa"
                className={`group relative overflow-hidden rounded-2xl border border-white/10 ${d.span}`}
                data-testid={`home-destination-${d.key}`}
              >
                <img src={d.img} alt={d.name} className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-venka-ink via-venka-ink/40 to-transparent" />
                <div className="absolute bottom-0 p-5">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-venka-green-light">{d.label}</p>
                  <h3 className="font-serif text-xl text-white mt-1 flex items-center gap-1">
                    {d.name} <ArrowUpRight className="h-4 w-4 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" />
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* WHY CHOOSE US + IMAGE */}
        <section className="mx-auto max-w-7xl px-6 py-24" data-testid="home-why-section">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-6 brand-gradient-soft blur-3xl rounded-full" />
              <img src={PASSPORT_IMG} alt="Traveller with passport" className="relative rounded-3xl border border-white/10 object-cover w-full h-[420px] shadow-2xl shadow-black/40" />
              <div className="absolute -bottom-6 -right-4 sm:right-6 glass-panel rounded-2xl border border-white/10 p-4 flex items-center gap-3 shadow-xl">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl brand-gradient text-white"><Plane className="h-5 w-5" /></span>
                <div>
                  <p className="text-sm font-semibold text-white">Ready for take-off</p>
                  <p className="text-xs text-slate-400">We handle the paperwork</p>
                </div>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold tracking-[0.22em] uppercase text-venka-green-light mb-3">Why VENKA</p>
              <h2 className="font-serif text-3xl md:text-4xl text-white leading-tight">A partner you can trust with every document</h2>
              <p className="mt-4 text-base text-slate-400">The most eminent travel and immigration consultant service provider, with branches in Angola and overseas — offering great quality service to all customers.</p>
              <div className="mt-8 space-y-5">
                {WHY.map((w, i) => {
                  const Icon = w.icon;
                  return (
                    <div key={i} className="flex gap-4" data-testid={`home-why-${i}`}>
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-venka-blue/15 text-venka-green-light"><Icon className="h-5 w-5" strokeWidth={1.8} /></span>
                      <div>
                        <h3 className="font-serif text-lg text-white">{w.title}</h3>
                        <p className="text-sm text-slate-400 mt-0.5">{w.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* TESTIMONIAL */}
        <section className="mx-auto max-w-7xl px-6 pb-24" data-testid="home-testimonials-section">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 brand-gradient p-10 md:p-16">
            <Quote className="absolute top-8 right-8 h-20 w-20 text-white/10" />
            <p className="text-xs font-semibold tracking-[0.22em] uppercase text-white/70 mb-4">Client story</p>
            <p className="font-serif text-2xl md:text-3xl text-white leading-relaxed max-w-3xl" data-testid="home-testimonial-quote">
              “It was great working with Venka World Express. Thank you very much for the effectiveness of your work — my visa process was smooth from start to finish.”
            </p>
            <div className="mt-6 flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white font-semibold">CI</span>
              <div>
                <p className="font-semibold text-white" data-testid="home-testimonial-name">Carmelha Ines</p>
                <p className="text-sm text-white/70" data-testid="home-testimonial-role">Verified customer</p>
              </div>
            </div>
          </div>
        </section>

        {/* CONTACT CTA */}
        <section id="contact" className="mx-auto max-w-7xl px-6 pb-28" data-testid="home-contact-strip">
          <div className="rounded-3xl border border-white/10 bg-card p-10 md:p-14 grid md:grid-cols-[1.4fr,1fr] gap-8 items-center">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl text-white leading-tight">Ready to start your application?</h2>
              <p className="mt-4 text-base text-slate-400 max-w-lg">Need a consultation? Reach out today and our team will guide your next steps. We serve customers in Angola, Europe and abroad.</p>
              <div className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
                <div className="rounded-xl border border-white/10 bg-background/40 p-4" data-testid="office-angola">
                  <p className="text-venka-green-light text-xs font-semibold uppercase tracking-wide">VENKA Angola</p>
                  <p className="text-slate-300 mt-1">Bloco 10, Prédio 12-A, apt. 302, Cidade do Sequele, Angola</p>
                </div>
                <div className="rounded-xl border border-white/10 bg-background/40 p-4" data-testid="office-europe">
                  <p className="text-venka-green-light text-xs font-semibold uppercase tracking-wide">VENKA Europe · Tallinn</p>
                  <p className="text-slate-300 mt-1">Harju maakond, Tallinn, Kesklinna linnaosa, Tartu mnt 67/1-13b, 10115</p>
                </div>
              </div>
              <div className="mt-6 flex flex-wrap gap-6 text-sm">
                {["angola", "intl"].map((k) => (
                  <a key={k} href={`tel:${PHONES[k].tel}`} className="flex items-center gap-2 text-slate-200 hover:text-white transition-colors" data-testid={k === "angola" ? "contact-phone" : "contact-phone-intl"}>
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-venka-blue/15 text-venka-green-light"><Phone className="h-4 w-4" /></span>
                    <span><span className="block text-[11px] text-slate-500">{PHONES[k].label.en}</span>{PHONES[k].display}</span>
                  </a>
                ))}
                <a href="mailto:sales@venkawe.com" className="flex items-center gap-2 text-slate-200 hover:text-white transition-colors" data-testid="contact-email">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-venka-blue/15 text-venka-green-light"><Mail className="h-4 w-4" /></span>
                  sales@venkawe.com
                </a>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <Button asChild size="lg" className="rounded-full brand-gradient text-white hover:opacity-90 shadow-xl shadow-venka-blue/25" data-testid="contact-apply-cta">
                <Link to="/apply">{t("apply.ctaOnline", "Apply Online")} <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full border-white/20 text-slate-100 hover:bg-white/5" data-testid="contact-page-cta">
                <Link to="/contact">All contact options</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full border-white/20 text-slate-100 hover:bg-white/5" data-testid="contact-visa-cta">
                <Link to="/visa">Start a visa request</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default HomePage;
